import type React from 'react';

import type { ColumnDef } from '@tanstack/react-table';
import type { GroupRecord, StreamRecord } from '@vlabs/api/artemy/model';

import {
  actionCell,
  analyticsStatusesCell,
  dividerCell,
  idCell,
  liveCell,
  locationCell,
  logsCell,
  nameCell,
  numberCell,
  renderCell,
  renderHeader,
  statusCell,
  StreamAnalyticsStatusesCellContent,
  StreamDeleteCellContent,
  StreamDuplicateCellContent,
  StreamEditCellContent,
  StreamIdCellContent,
  StreamLiveCellContent,
  StreamLocationCellContent,
  StreamLogsCellContent,
  StreamNameCellContent,
  StreamNumberCellContent,
  StreamStatusWithActionsCellContent,
} from './cells/cells';

interface BuildStreamsColumnsParams {
  /** t с ns виджета таблицы (sourceGroupListViewer) — заголовки колонок. */
  t: (key: string) => string;
  group: GroupRecord;
  /** Режим отложенного удаления (страница группы). */
  onEnqueueDelete?: (streamIds: string[]) => void;
}

/** Колонки таблицы потоков — макет един для списка источников и страницы группы. */
export function buildStreamsColumns({
  t,
  group,
  onEnqueueDelete = undefined,
}: BuildStreamsColumnsParams): ColumnDef<StreamRecord>[] {
  return [
    {
      id: 'number',
      header: renderHeader(numberCell, t('columns.number')),
      cell: renderCell<StreamRecord, unknown>(numberCell, (ctx) => (
        <StreamNumberCellContent index={ctx.row.index} />
      )),
    },
    {
      accessorKey: 'name',
      id: 'name',
      header: renderHeader(nameCell, t('columns.name')),
      cell: renderCell<StreamRecord, unknown>(nameCell, (ctx) => (
        <StreamNameCellContent
          group={group}
          stream={ctx.row.original}
        />
      )),
    },
    {
      accessorKey: 'live',
      id: 'live',
      header: renderHeader(liveCell, t('columns.live')),
      cell: renderCell<StreamRecord, unknown>(liveCell, (ctx) => (
        <StreamLiveCellContent stream={ctx.row.original} />
      )),
    },
    {
      accessorKey: 'status',
      id: 'status',
      header: renderHeader(statusCell, t('columns.status')),
      cell: renderCell<StreamRecord, unknown>(statusCell, (ctx) => (
        <StreamStatusWithActionsCellContent stream={ctx.row.original} />
      )),
    },
    {
      accessorKey: 'analytics',
      id: 'analytics',
      header: renderHeader(analyticsStatusesCell, t('columns.analytics_statuses')),
      cell: renderCell<StreamRecord, unknown>(analyticsStatusesCell, (ctx) => (
        <StreamAnalyticsStatusesCellContent stream={ctx.row.original} />
      )),
    },
    {
      accessorKey: 'location',
      id: 'location',
      header: renderHeader(locationCell, t('columns.location')),
      cell: renderCell<StreamRecord, unknown>(locationCell, (ctx) => (
        StreamLocationCellContent({ stream: ctx.row.original })
      )),
    },
    {
      accessorKey: 'stream_id',
      id: 'stream_id',
      header: renderHeader(idCell, t('columns.id')),
      cell: renderCell<StreamRecord, unknown>(idCell, (ctx) => (
        StreamIdCellContent({ stream: ctx.row.original })
      )),
    },
    {
      accessorKey: 'logs',
      id: 'logs',
      header: renderHeader(logsCell, t('columns.logs')),
      cell: renderCell<StreamRecord, unknown>(logsCell, (ctx) => (
        <StreamLogsCellContent stream={ctx.row.original} />
      )),
    },
    {
      accessorKey: 'edit',
      id: 'edit',
      header: renderHeader(actionCell),
      cell: renderCell<StreamRecord, unknown>(actionCell, (ctx) => (
        <StreamEditCellContent stream={ctx.row.original} />
      )),
    },
    {
      accessorKey: 'duplicate',
      id: 'duplicate',
      header: renderHeader(actionCell),
      cell: renderCell<StreamRecord, unknown>(actionCell, (ctx) => (
        <StreamDuplicateCellContent
          groupId={group.group_id}
          stream={ctx.row.original}
        />
      )),
    },
    {
      accessorKey: 'delete',
      id: 'delete',
      header: renderHeader(actionCell),
      cell: renderCell<StreamRecord, unknown>(actionCell, (ctx) => (
        <StreamDeleteCellContent
          onEnqueueDelete={onEnqueueDelete}
          stream={ctx.row.original}
        />
      )),
    },
    {
      id: 'divider',
      header: renderHeader(dividerCell),
      cell: renderCell(dividerCell),
    },
  ] as ColumnDef<StreamRecord>[];
}
